import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BackupService } from '../../services/backup.service';
import { DownloadManagerService } from '../../services/download-manager.service';
import { Subscription } from 'rxjs';

// Simple interfaces for Angular 7
interface Backup {
  id: number;
  status: string;
  created_at: string;
  started_at?: string;
  ended_at?: string;
  downloaded_at?: string;
  deleted_at?: string;
  download_link?: string;
  file_size?: number;
  error_message?: string;
}

interface DownloadState {
  progress: number;
  downloadedBytes: number;
  totalBytes: number;
  status: string;
  error?: string;
  canResume?: boolean;
  estimatedTimeRemaining?: number;
  downloadSpeed?: number;
}

@Component({
  selector: 'app-manage-backups',
  templateUrl: './manage-backups.component.html',
  styleUrls: ['./manage-backups.component.css']
})
export class ManageBackupsComponent implements OnInit, OnDestroy {

  @ViewChild('modalCreateBackup') modalCreateBackup: any;

  // Form management
  backupForm: FormGroup;

  // Data management
  backups: Backup[] = [];
  totalBackups = 0;
  currentPage = 1;
  pageSize = 6;

  // Download state management
  downloadStates = new Map<number, DownloadState>();
  downloadStartTimes = new Map<number, number>();

  // Date management
  today = new Date();
  currentDate: string;
  minStartDate: string;
  maxStartDate: string;

  // Subscriptions
  private subscriptions: Subscription[] = [];
  private refreshWarningShown = false;

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    public backupService: BackupService,
    private downloadManager: DownloadManagerService
  ) {
    this.currentDate = this.dateToString(this.today);
    this.minStartDate = null;
    this.maxStartDate = this.dateToString(this.today);

    this.initializeForm();
  }

  /**
   * Browser refresh warning
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.backupService.hasActiveDownloads()) {
      event.preventDefault();
      event.returnValue = 'There is a download in progress. If you leave the page, the progress will be lost.';
    }
  }

  /**
   * Handle visibility changes
   */
  @HostListener('document:visibilitychange', ['$event'])
  handleVisibilityChange(): void {
    if (!document.hidden && !this.refreshWarningShown) {
      this.checkForActiveDownloads();
    }
  }

  /**
   * Component initialization
   */
  ngOnInit(): void {
    this.loadBackups();
    this.subscribeToDownloadUpdates();
    this.checkForActiveDownloads();
    this.showRefreshWarningIfNeeded();
  }

  /**
   * Component cleanup
   */
  ngOnDestroy(): void {
    for (let i = 0; i < this.subscriptions.length; i++) {
      if (this.subscriptions[i] && !this.subscriptions[i].closed) {
        this.subscriptions[i].unsubscribe();
      }
    }
  }

  /**
   * Initialize form
   */
  private initializeForm(): void {
    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    const monthAgoStr = this.dateToString(monthAgo);

    this.backupForm = this.fb.group({
      fullBackup: [false],
      startDate: [{ value: monthAgoStr, disabled: false }, Validators.required],
      endDate: [{ value: this.currentDate, disabled: false }, Validators.required]
    });
  }

  /**
   * Subscribe to download updates
   */
  private subscribeToDownloadUpdates(): void {
    const self = this;
    const downloadSub = this.downloadManager.getAllDownloads().subscribe(function(downloadsMap) {
      // Convert Map to handle properly
      const entries = Array.from(downloadsMap.entries());
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const backupId = entry[0];
        const downloadProgress = entry[1];

        const state: DownloadState = {
          progress: downloadProgress.progress,
          downloadedBytes: downloadProgress.downloadedBytes,
          totalBytes: downloadProgress.totalBytes,
          status: downloadProgress.status,
          error: downloadProgress.error,
          canResume: downloadProgress.canResume
        };

        // Calculate download speed and ETA
        if (state.status === 'downloading') {
          const startTime = self.downloadStartTimes.get(backupId);
          if (startTime) {
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            state.downloadSpeed = state.downloadedBytes / elapsedSeconds;

            if (state.downloadSpeed > 0) {
              const remainingBytes = state.totalBytes - state.downloadedBytes;
              state.estimatedTimeRemaining = Math.ceil(remainingBytes / state.downloadSpeed);
            }
          }
        }

        self.downloadStates.set(backupId, state);
      }
    });

    this.subscriptions.push(downloadSub);
  }

  /**
   * Check for active downloads
   */
  private checkForActiveDownloads(): void {
    const self = this;
    setTimeout(function() {
      const activeDownloads = self.backupService.getDownloadStats();
      if (activeDownloads.active > 0) {
        // console.log('Found ' + activeDownloads.active + ' active background downloads');
      }
    }, 1000);
  }

  /**
   * Show refresh warning if needed
   */
  private showRefreshWarningIfNeeded(): void {
    if (this.backupService.hasActiveDownloads() && !this.refreshWarningShown) {
      this.refreshWarningShown = true;
      alert('Downloads ativos detectados. Alguns downloads podem ter sido interrompidos pela atualização da página.');
    }
  }

  /**
   * Open modal
   */
  openModal(): void {
    this.modalService.open(this.modalCreateBackup, { size: 'lg' });
  }

  /**
   * Close modal
   */
  closeModal(): void {
    this.modalService.dismissAll();
    this.backupForm.reset({
      fullBackup: false,
      startDate: this.currentDate,
      endDate: this.currentDate
    });
    this.toggleDateFields();
  }

  /**
   * Request backup
   */
  requestBackup(): void {
    this.markFormGroupTouched(this.backupForm);

    if (!this.backupForm.valid) {
      alert('Por favor, preencha todos os campos obrigatórios corretamente.');
      return;
    }

    const formValue = this.backupForm.value;
    const fullBackup = formValue.fullBackup;
    const startDate = formValue.startDate;
    const endDate = formValue.endDate;
    const businessUnitId = this.getBusinessUnitId();
    const userId = this.getUserId();

    const backupRequest: any = { businessUnitId: businessUnitId, userId: userId };

    if (fullBackup) {
      backupRequest.full = true;
    } else {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        alert('A data de início não pode ser posterior à data de término.');
        return;
      }

      backupRequest.dateStart = startDate;
      backupRequest.dateEnd = endDate;
      backupRequest.full = false;
    }

    const self = this;
    this.backupService.createBackup(backupRequest).subscribe({
      next: function() {
        self.loadBackups();
        self.closeModal();
      },
      error: function(error) {
        if (error.status === 400) {
          const errorDetail = (error && error.error && error.error.detail);
          let message = errorDetail || 'Um erro ocorreu ao tentar criar o backup.';
          if (errorDetail === 'A backup request is already in progress for this user.') {
            message = 'Uma solicitação de backup já está em andamento para este usuário.';
          }
          if (errorDetail === 'No files found on this period.') {
            message = 'Nenhum arquivo encontrado neste período.';
          }

          alert(message);
          self.closeModal();
          return;
        }

        alert('Erro ao solicitar backup: ' + (error.message || 'Ops! Algo deu errado. Tente novamente mais tarde.'));
        self.closeModal();
      }
    });
  }

  /**
   * Load backups
   */
  loadBackups(): void {
    const businessUnitId = this.getBusinessUnitId();
    const self = this;

    this.backupService.getBackups(businessUnitId, this.currentPage, this.pageSize).subscribe(function(response: any) {
      const backupsData = response.backups;
      self.totalBackups = backupsData.total;
      self.backups = backupsData.data;

      // Initialize download states
      for (let i = 0; i < self.backups.length; i++) {
        const backup = self.backups[i];
        if (!self.downloadStates.has(backup.id)) {
          self.downloadStates.set(backup.id, {
            progress: 0,
            downloadedBytes: 0,
            totalBytes: 0,
            status: 'idle'
          });
        }
      }
    });
  }

  /**
   * Download file new version
   */
  downloadFileOnNewLink(event: MouseEvent, backup: Backup): void {
    event.preventDefault();

    const validation = this.backupService.validateBackupForDownload(backup);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    window.open(backup.download_link, '_blank', 'noopener,noreferrer');
  }

  /**
   * Download file
   */
  downloadFile(event: MouseEvent, backup: Backup): void {
    event.preventDefault();

    const validation = this.backupService.validateBackupForDownload(backup);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    const id = backup.id;
    const filename = 'backup-' + id + '.zip';

    // Check if can resume
    const currentState = this.downloadStates.get(id);
    if (currentState && currentState.canResume && currentState.downloadedBytes > 0) {
      this.resumeDownload(event, backup);
      return;
    }

    // Track start time
    this.downloadStartTimes.set(id, Date.now());

    const self = this;
    this.backupService.startOptimizedDownload(
      id,
      backup.download_link,
      filename,
      function(percent, downloadedBytes, totalBytes) {
        // Progress handled by subscription
      },
      function() {
        // console.log('Download completed for backup ' + id);
        self.downloadStartTimes.delete(id);
      },
      function(error) {
        console.error('Download failed for backup ' + id + ':', error);
        alert('Ops! Falha ao fazer o Download: ' + error);
      }
    );
  }

  /**
   * Resume download
   */
  resumeDownload(event: MouseEvent, backup: Backup): void {
    event.preventDefault();

    const id = backup.id;
    const filename = 'backup-' + id + '.zip';

    this.downloadStartTimes.set(id, Date.now());

    const self = this;
    this.backupService.resumeDownload(
      id,
      backup.download_link,
      filename,
      function(percent, downloadedBytes, totalBytes) {
        // Progress handled by subscription
      },
      function() {
        // console.log('Download resumed and completed for backup ' + id);
        self.downloadStartTimes.delete(id);
      },
      function(error) {
        // console.error('Resume failed for backup ' + id + ':', error);
        alert('Resume failed: ' + error);
      }
    );
  }

  /**
   * Pause download
   */
  pauseDownload(event: MouseEvent, backupId: number): void {
    event.preventDefault();
    this.backupService.pauseDownload(backupId);
  }

  /**
   * Cancel download
   */
  cancelDownload(event: MouseEvent, backupId: number): void {
    event.preventDefault();
    if (confirm('Are you sure you want to cancel this download? Progress will be lost.')) {
      this.backupService.cancelDownload(backupId);
      this.downloadStartTimes.delete(backupId);
    }
  }

  /**
   * Update backup status
   */
  updateBackupStatus(backupId: string, status: string, updateData: any = {}): void {
    const data = Object.assign({ status: status }, updateData);
    const self = this;

    this.backupService.updateBackup(backupId, data).subscribe(function() {
      self.loadBackups();
    }, function(error) {
      // console.error('Error updating backup status:', error);
      alert('Falha ao atualizar o status do backup.' + (error.message || ''));
    });
  }

  /**
   * Retry backup
   */
  retryBackup(id: string): void {
    this.updateBackupStatus(id, 'created');
  }

  /**
   * Get download state
   */
  getDownloadState(backupId: number): DownloadState {
    return this.downloadStates.get(backupId) || {
      progress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      status: 'idle'
    };
  }

  /**
   * Check if download is active
   */
  isDownloadActive(backupId: number): boolean {
    const state = this.getDownloadState(backupId);
    return state.status === 'downloading';
  }

  /**
   * Check if can resume
   */
  canResumeDownload(backupId: number): boolean {
    const state = this.getDownloadState(backupId);
    return (state.status === 'paused' || state.status === 'error') && state.canResume === true;
  }

  /**
   * Format bytes
   */
  formatBytes(bytes: number): string {
    return this.backupService.formatFileSize(bytes);
  }

  /**
   * Format download speed
   */
  formatDownloadSpeed(bytesPerSecond: number): string {
    return this.backupService.formatFileSize(bytesPerSecond) + '/s';
  }

  /**
   * Format time remaining
   */
  formatTimeRemaining(seconds: number): string {
    if (!seconds || seconds <= 0) { return 'Calculating...'; }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return hours + 'h ' + minutes + 'm ' + secs + 's';
    } else if (minutes > 0) {
      return minutes + 'm ' + secs + 's';
    } else {
      return secs + 's';
    }
  }

  /**
   * Translate status
   */
  translateStatus(status: string): { label: string; icon: string; color: string; title: string } {
    const statusMap: any = {
      'created': { label: 'Em fila de processamento', icon: 'fa fa-clock', color: 'text-primary', title: '' },
      'processing': { label: 'Em andamento', icon: 'fa fa-spinner fa-spin', color: 'text-warning', title: '' },
      'finished': { label: 'Completo', icon: 'fa fa-check-circle', color: 'text-success', title: '' },
      'failed': { label: 'Falhou', icon: 'fa fa-times-circle', color: 'text-danger', title: '' },
      'closed': { label: 'Expirado', icon: 'fa fa-exclamation-circle', color: 'text-muted', title: 'Expirou o prazo que o backup gerado poderia ser baixado, se precisar novamente basta solicitar um novo.' },
      'DOWNLOADED': { label: 'Download iniciado', icon: 'fa fa-download', color: 'text-info', title: '' }
    };
    return statusMap[status] || { label: 'Status Desconhecido', icon: 'fa fa-question-circle', color: 'text-muted', title: '' };
  }

  /**
   * Track by ID
   */
  trackById(index: number, item: Backup): number {
    return item.id;
  }

  /**
   * Toggle date fields
   */
  toggleDateFields(): void {
    const fullBackupControl = this.backupForm.get('fullBackup');
    const startDateControl = this.backupForm.get('startDate');
    const endDateControl = this.backupForm.get('endDate');

    if (!fullBackupControl || !startDateControl || !endDateControl) {
      return;
    }

    const isFullBackup = fullBackupControl.value;

    if (isFullBackup) {
      startDateControl.disable();
      endDateControl.disable();
      startDateControl.patchValue(null);
      endDateControl.patchValue(null);
      return;
    }

    startDateControl.enable();
    startDateControl.patchValue(this.currentDate);
    endDateControl.enable();
    endDateControl.patchValue(this.currentDate);
  }

  /**
   * Get backup file size
   */
  getBackupFileSize(backup: Backup): string {
    if (backup.file_size) {
      return this.formatBytes(backup.file_size);
    }

    const downloadState = this.getDownloadState(backup.id);
    if (downloadState.totalBytes > 0) {
      return this.formatBytes(downloadState.totalBytes);
    }

    return 'Unknown';
  }

  /**
   * Get download button text
   */
  getDownloadButtonText(backup: Backup): string {
    const state = this.getDownloadState(backup.id);

    if (state.status === 'downloading') { return 'Downloading...'; }
    if (state.status === 'paused') { return 'Resume Download'; }
    if (state.status === 'error') { return 'Retry Download'; }
    if (state.status === 'completed') { return 'Download Again'; }
    return 'Download Backup';
  }

  /**
   * Get download button icon
   */
  getDownloadButtonIcon(backup: Backup): string {
    const state = this.getDownloadState(backup.id);

    if (state.status === 'downloading') { return 'fa fa-spinner fa-spin'; }
    if (state.status === 'paused') { return 'fa fa-play'; }
    if (state.status === 'error') { return 'fa fa-retry'; }
    if (state.status === 'completed') { return 'fa fa-download'; }
    return 'fa fa-download';
  }

  /**
   * Get download button class
   */
  getDownloadButtonClass(backup: Backup): string {
    const state = this.getDownloadState(backup.id);

    if (state.status === 'downloading') { return 'btn btn-sm btn-warning'; }
    if (state.status === 'paused') { return 'btn btn-sm btn-info'; }
    if (state.status === 'error') { return 'btn btn-sm btn-danger'; }
    if (state.status === 'completed') { return 'btn btn-sm btn-success'; }
    return 'btn btn-sm btn-info';
  }

  /**
   * Show download stats
   */
  showDownloadStats(): void {
    const stats = this.backupService.getDownloadStats();
    const message = 'Download Statistics:\n' +
      '- Total Downloads: ' + stats.total + '\n' +
      '- Active: ' + stats.active + '\n' +
      '- Completed: ' + stats.completed + '\n' +
      '- Paused: ' + stats.paused + '\n' +
      '- Errors: ' + stats.errors;
    alert(message);
  }

  /**
   * Clear completed downloads
   */
  clearCompletedDownloads(): void {
    if (confirm('Clear all completed downloads from the list?')) {
      const entries = Array.from(this.downloadStates.entries());
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const backupId = entry[0];
        const state = entry[1];
        if (state.status === 'completed') {
          this.downloadStates.delete(backupId);
        }
      }
    }
  }

  /**
   * Check if backup can be downloaded
   */
  canDownload(backup: Backup): boolean {
    return !!(backup.download_link && backup.status === 'finished' && !backup.deleted_at);
  }

  /**
   * Get progress bar color
   */
  getProgressBarColor(backupId: number): string {
    const state = this.getDownloadState(backupId);

    if (state.status === 'downloading') { return 'progress-bar-info'; }
    if (state.status === 'paused') { return 'progress-bar-warning'; }
    if (state.status === 'error') { return 'progress-bar-danger'; }
    if (state.status === 'completed') { return 'progress-bar-success'; }
    return 'progress-bar-info';
  }

  // Utility methods

  /**
   * Convert Date to string
   */
  private dateToString(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Get business unit ID
   */
  private getBusinessUnitId(): number | null {
    const user = this.getUserFromLocalStorage();
    return user && user.businessUnit ? user.businessUnit.id : null;
  }

  /**
   * Get user ID
   */
  private getUserId(): number | null {
    const user = this.getUserFromLocalStorage();
    return user ? user.id : null;
  }

  /**
   * Get user from localStorage
   */
  private getUserFromLocalStorage(): any | null {
    const user = localStorage.getItem('user');
    try {
      return user ? JSON.parse(user) : null;
    } catch (e) {
      console.error('Error parsing user data from localStorage', e);
      return null;
    }
  }

  /**
   * Mark form as touched
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    const keys = Object.keys(formGroup.controls);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const control = formGroup.get(key);
      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    }
  }
}
